import { useCallback, useContext, useDeferredValue, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import { cardFullWidth } from '@/utils/styleUtils';
import ChartControls from './PerformanceChart/ChartControls';
import PostLaunchComparison from './PostLaunchComparison';
import {
  SelectableMetricsContext,
  SelectableMetricsView,
  ServerSidePaginatedTable,
} from './PerformanceTable';
import { FilterParams, useFilters } from '@/services/revenueApi';
import PerformanceChart from './PerformanceChart';
import {
  ChartGranularity,
  DailyMetric,
  EpisodePublishDateBucket,
  EpisodePublishDatesResponse,
  LaunchWindowDays,
} from '@/types/content';
import type { Episode, NetworkPerformanceRow, Show, SortField } from '@/types';
import { formatTotalsTabLabel, getDateString, MIXPANEL_EVENT } from '@/utils';
import { trackContentPerformanceEventIfApplicable } from '../trackingUtils';
import { useQueryUrl } from '@/services/queryUtils';
import { Flex, Table, Tabs as AntTabs, Tag, Typography, theme } from 'antd';
import type { ColumnType } from 'antd/es/table';
import {
  episodeMetricsBreakdownUrlBuilder,
  episodeMetricsUrlBuilder,
  episodesLaunchWindowUrlBuilder,
  episodesUrlBuilder,
  networkMetricsUrlBuilder,
  networksUrlBuilder,
  publishDatesUrlBuilder,
  showMetricsUrlBuilder,
  showMetricsBreakdownUrlBuilder,
  showsUrlBuilder,
  type MetricQueryParams,
  type SelectedItemParams,
  dataSource,
} from './performanceCardUrlBuilders';
import { ExpandedMetricRowRenderEcho } from './PerformanceTable/ExpandedMetricRowRender';
import { getMetricColumns } from './PerformanceTable/metricColumns';
import { AvatarLabelCell } from './PerformanceTable/AvatarLabelCell';
import { DateString } from '@/types/state/filter';

type PerformanceCardProps = {
  /** `true` after the first successful sync; use as an `enabled` guard on data queries. */
  isSynced?: boolean;
  /** Card heading. Defaults to "Performance". */
  title?: string;
  /** `false` when no HLS-enabled show is selected; video metrics are always zero, so their columns are hidden. */
  showVideoMetrics?: boolean;
};

const EMPTY_BUCKETS: EpisodePublishDateBucket[] = [];

/** Hard-cap a column so table-layout:fixed cannot redistribute its space. */
const lockColumnWidth = (width: number) => ({
  width,
  onHeaderCell: () => ({ style: { width, minWidth: width, maxWidth: width } }),
  onCell: () => ({ style: { width, minWidth: width, maxWidth: width } }),
});

/** Min scroll width shared by episode/show tables so metric columns stay aligned across tabs. */
const PERFORMANCE_TABLE_SCROLL_X = 1120;

/** Show-level-access network rows get real zeros from the API; show a dash instead so it doesn't read as "zero plays". */
const withDashForShowLevelAccess = (
  columns: Array<ColumnType<NetworkPerformanceRow>>
): Array<ColumnType<NetworkPerformanceRow>> =>
  columns.map((column) => ({
    ...column,
    render: (value: unknown, record: NetworkPerformanceRow, index: number) =>
      record.accessLevel === 'show' ? '–' : column.render?.(value, record, index),
  }));

type PerformanceTabProps = {
  isSynced?: boolean;
  dailyMetricsUrlBuilder: (params: MetricQueryParams & SelectedItemParams) => string;
  /** When set, shows the Display total toggle and fetches the total series (legacy chart only). */
  totalMetricsUrlBuilder?: (params: MetricQueryParams) => string;
  publishDatesUrlBuilder?: (filters: FilterParams) => string;
};

/** Which tab of {@link EpisodePerformanceCardEcho} is active. */
type ActiveTab = 'totals' | 'post-launch';

/**
 * "Downloads & views" is hidden without video metrics; fall back to "Audio downloads" (same order,
 * since there are no views) so the visible column carries the sort indicator.
 */
const metricDefaultSort = (showVideoMetrics: boolean): SortField => ({
  key: showVideoMetrics ? 'downloadsAndViews' : 'audioDownloads',
  direction: 'desc',
});

/** Chart granularity/display controls plus the chart itself. Shared by both cards. */
const ChartSection = ({
  isSynced = true,
  dailyMetricsUrlBuilder,
  totalMetricsUrlBuilder,
  publishDatesUrlBuilder,
}: PerformanceTabProps) => {
  const [granularity, setGranularity] = useState<ChartGranularity>('day');
  const [displayPublishDates, setDisplayPublishDates] = useState(true);
  const [displayTotal, setDisplayTotal] = useState(false);
  const showTotalToggle = Boolean(totalMetricsUrlBuilder);

  const { selectedItems, hasTouched, isListEmpty } = useContext(SelectableMetricsContext);

  // Defer the selection that drives the (expensive, memoized) chart render so toggling a checkbox
  // paints instantly; warm the metrics cache with the live selection so the deferred render is a
  // cache hit rather than a fresh fetch. See the `deferred-expensive-renders` skill.
  const deferredSelectedItems = useDeferredValue(selectedItems);

  const filters = useFilters();

  // `enabled` is what stops the fetch when the table has no selection.
  const dailyMetricsResult = useQueryUrl<
    ({ date: DateString } & { [id: string]: number })[],
    MetricQueryParams & SelectedItemParams
  >(
    dailyMetricsUrlBuilder,
    {
      filters,
      granularity,
      selectedItems: deferredSelectedItems,
    },
    {
      enabled: deferredSelectedItems.length > 0 && isSynced,
      // Keep the last series on screen while a new selection fetches.
      placeholderData: keepPreviousData,
    }
  );

  const totalMetricsResult = useQueryUrl<DailyMetric[], MetricQueryParams>(
    totalMetricsUrlBuilder,
    {
      filters,
      granularity,
    },
    { enabled: showTotalToggle && displayTotal && isSynced }
  );

  const publishDatesResult = useQueryUrl<EpisodePublishDatesResponse, FilterParams>(
    publishDatesUrlBuilder,
    filters,
    { enabled: Boolean(publishDatesUrlBuilder && displayPublishDates) && isSynced }
  );

  const publishBuckets = useMemo(
    () => publishDatesResult.data?.buckets ?? EMPTY_BUCKETS,
    [publishDatesResult.data]
  );

  // `suspendOnPending={false}`: CardContent keeps children mounted; PerformanceChart owns the
  // slot spinner. Don't use combineQueryStatuses here — disabled queries look `isPending`, which
  // would suppress errors from sibling queries. Pass honest metrics pending + first error instead.
  // Drop cached points when selection is empty so clear-all shows the empty CTA, not bare axes.
  const hasSelection = selectedItems.length > 0;
  const metrics = hasSelection ? (dailyMetricsResult.data ?? []) : [];
  // `isListEmpty` is false while the list is pending, so this also covers first load.
  const isAwaitingSeed = !hasTouched && !hasSelection && !isListEmpty;
  const isMetricsFetchPending =
    hasSelection &&
    (selectedItems !== deferredSelectedItems ||
      dailyMetricsResult.isPending ||
      dailyMetricsResult.isPlaceholderData);
  const isChartLoading = isAwaitingSeed || isMetricsFetchPending;
  const chartError = [dailyMetricsResult, totalMetricsResult, publishDatesResult].find(
    (query) => query.isError
  );

  return (
    <>
      <ChartControls
        granularity={granularity}
        onGranularityChange={setGranularity}
        displayPublishDates={displayPublishDates}
        onDisplayPublishDatesChange={setDisplayPublishDates}
        {...(showTotalToggle ? { displayTotal, onDisplayTotalChange: setDisplayTotal } : {})}
      />
      <CardContent
        suspendOnPending={false}
        isPending={isChartLoading}
        isError={Boolean(chartError)}
        error={chartError?.error ?? null}
        data={metrics}
        renderData={(dailyMetrics) => (
          <PerformanceChart
            selectedItems={deferredSelectedItems}
            publishBuckets={publishBuckets}
            dailyMetrics={dailyMetrics}
            granularity={granularity}
            displayPublishDates={displayPublishDates}
            displayTotal={showTotalToggle && displayTotal}
            totalDailyMetrics={totalMetricsResult?.data}
            dateRange={filters.dateRange}
            isLoading={isChartLoading}
          />
        )}
      />
    </>
  );
};

export const NetworksPerformanceCardEcho = ({
  isSynced = true,
  showVideoMetrics = true,
}: Pick<PerformanceCardProps, 'isSynced' | 'showVideoMetrics'>) => {
  return (
    <CardShellEcho
      title="Network Performance"
      testId="networks-performance-card"
      className={cardFullWidth}
    >
      <SelectableMetricsView>
        <Flex vertical gap="large">
          <ChartSection
            isSynced={isSynced}
            dailyMetricsUrlBuilder={networkMetricsUrlBuilder}
            publishDatesUrlBuilder={publishDatesUrlBuilder}
          />
          <ServerSidePaginatedTable<NetworkPerformanceRow>
            isSynced={isSynced}
            urlBuilder={networksUrlBuilder}
            defaultSort={metricDefaultSort(showVideoMetrics)}
            columns={[
              Table.SELECTION_COLUMN,
              { title: 'Network', dataIndex: 'label', sorter: true },
              ...withDashForShowLevelAccess(getMetricColumns('network', { showVideoMetrics })),
            ]}
            isRowDisabled={(row) => row.accessLevel === 'show'}
            disabledRowTooltip="You only have show-level access to this network, so it can't be selected here."
          />
        </Flex>
      </SelectableMetricsView>
    </CardShellEcho>
  );
};

export const ShowsPerformanceCardEcho = ({
  isSynced = true,
  showVideoMetrics = true,
}: Pick<PerformanceCardProps, 'isSynced' | 'showVideoMetrics'>) => {
  const filters = useFilters();

  // Match Episodes pre-metric widths (Show 160 + Status 120 + Publish date 140 = 420) so
  // Downloads & views / Audio downloads / Video views stay aligned across tabs when scrolling.
  const showPerformanceColumns = [
    Table.SELECTION_COLUMN,
    {
      title: 'Show',
      dataIndex: 'label',
      key: 'label',
      sorter: true,
      ellipsis: true,
      // Flexible column
      onHeaderCell: () => ({ style: { minWidth: 180 } }),
      onCell: () => ({ style: { minWidth: 180 } }),
      render: (_: unknown, show: Show) => (
        <AvatarLabelCell label={show.label} imageUrl={show.imageUrl} />
      ),
    },
    {
      title: 'Network',
      dataIndex: 'networkName',
      key: 'networkName',
      sorter: true,
      ellipsis: true,
      ...lockColumnWidth(420),
      render: (networkName: string) => (
        <Typography.Text ellipsis={{ tooltip: networkName }}>{networkName}</Typography.Text>
      ),
    },
    ...getMetricColumns('show', { showVideoMetrics }),
    Table.EXPAND_COLUMN,
  ];

  return (
    <CardShellEcho
      title="Shows Performance"
      testId="shows-performance-card"
      className={cardFullWidth}
    >
      <SelectableMetricsView>
        <Flex vertical gap="large">
          <ChartSection
            isSynced={isSynced}
            dailyMetricsUrlBuilder={showMetricsUrlBuilder}
            publishDatesUrlBuilder={publishDatesUrlBuilder}
          />
          <ServerSidePaginatedTable<Show>
            isSynced={isSynced}
            urlBuilder={showsUrlBuilder}
            defaultSort={metricDefaultSort(showVideoMetrics)}
            columns={showPerformanceColumns}
            scrollX={PERFORMANCE_TABLE_SCROLL_X}
            expandedRowRender={(item) => (
              <ExpandedMetricRowRenderEcho<Show>
                item={item}
                urlBuilder={showMetricsBreakdownUrlBuilder}
                parentTableColumns={showPerformanceColumns}
                filters={filters}
              />
            )}
          />
        </Flex>
      </SelectableMetricsView>
    </CardShellEcho>
  );
};

/**
 * Episode performance card (Echo/simplified table): adds a tab layer over {@link PerformanceCard},
 * switching between the totals view and the post-launch comparison. Each branch renders its own
 * `SelectableMetricsView` + table, so switching tabs unmounts one and mounts the other — each
 * tab's selection/nav state is self-contained. The Totals branch is date-scoped; Post-Launch
 * ("All time") is date-independent.
 *
 * Used by the Episodes tab in {@link DataLevelTabs}.
 */
const EpisodePerformanceCardEcho = ({
  title = 'Performance',
  isSynced = true,
  showVideoMetrics = true,
}: PerformanceCardProps) => {
  const { token } = theme.useToken();
  const [activeTab, setActiveTab] = useState<ActiveTab>('totals');
  const [launchWindow, setLaunchWindow] = useState<LaunchWindowDays>(7);

  const filters = useFilters();

  const { from: dateFrom, to: dateTo } = filters.dateRange!;
  const totalsTabLabel = formatTotalsTabLabel(dateFrom, dateTo);

  const handleTabChange = useCallback((tabId: string) => {
    const tab = tabId as ActiveTab;
    trackContentPerformanceEventIfApplicable(
      'content-performance',
      MIXPANEL_EVENT.contentPerformance.chart.tabChanged,
      { tab }
    );
    setActiveTab(tab);
  }, []);

  /**
   * Episode/Status/Publish-date + metric columns shared by the Totals and Post-Launch tabs, so
   * the two can't drift apart. Totals adds the Show column and the expand/platforms drill-down;
   * Post-Launch (launch-window-scoped metrics, no platform breakdown) omits both.
   */
  const buildEpisodeColumns = (options?: {
    metricOptions?: { sortable?: boolean; labelSuffix?: string };
    includeShow?: boolean;
    includeExpand?: boolean;
  }) => {
    const { metricOptions, includeShow = true, includeExpand = true } = options ?? {};
    // False for Post-Launch, whose launch-window endpoint can't sort by arbitrary columns.
    const nonDateSortable = metricOptions?.sortable ?? true;
    return [
      Table.SELECTION_COLUMN,
      {
        title: 'Episode',
        dataIndex: 'label',
        key: 'label',
        sorter: nonDateSortable,
        ellipsis: true,
        // Flexible column — absorbs leftover space so titles get room
        // while Show and metric columns keep their fixed sizes.
        onHeaderCell: () => ({ style: { minWidth: 180 } }),
        onCell: () => ({ style: { minWidth: 180 } }),
        render: (_: unknown, episode: Episode) => (
          <AvatarLabelCell label={episode.label} imageUrl={episode.imageUrl} />
        ),
      },
      ...(includeShow
        ? [
            {
              title: 'Show',
              key: 'showName',
              sorter: true,
              ellipsis: true,
              ...lockColumnWidth(160),
              render: (_: unknown, episode: Episode) => (
                <AvatarLabelCell label={episode.showName ?? ''} imageUrl={episode.showImageUrl} />
              ),
            },
          ]
        : []),
      {
        title: 'Status',
        dataIndex: 'unpublished',
        sorter: nonDateSortable,
        ...lockColumnWidth(120),
        render: (unpublished: boolean) =>
          unpublished ? <Tag>Unpublished</Tag> : <Tag color="success">Published</Tag>,
      },
      {
        title: 'Publish date',
        dataIndex: 'publishDate',
        sorter: true,
        align: 'right',
        ...lockColumnWidth(140),
        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        render: (publishDate: string) => (
          <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {getDateString(publishDate)}
          </Typography.Text>
        ),
      },
      ...getMetricColumns('episode', { showVideoMetrics, ...metricOptions }),
      ...(includeExpand ? [Table.EXPAND_COLUMN] : []),
    ];
  };

  const episodePerformanceColumns = buildEpisodeColumns();

  const postLaunchColumns = buildEpisodeColumns({
    metricOptions: { sortable: false, labelSuffix: ` (${launchWindow} days)` },
    includeShow: false,
    includeExpand: false,
  });

  return (
    <CardShellEcho
      title={title}
      testId="performance-card"
      className={cardFullWidth}
    >
      <AntTabs
        // Unmounts the inactive tab's content — same as DataLevelTabs — so each tab's
        // SelectableMetricsView remounts fresh (self-contained, reset-on-return selection state).
        destroyOnHidden
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'totals',
            label: totalsTabLabel,
            children: (
              <SelectableMetricsView>
                <Flex vertical gap="large">
                  <ChartSection
                    isSynced={isSynced}
                    dailyMetricsUrlBuilder={dataSource('v3', episodeMetricsUrlBuilder)}
                    publishDatesUrlBuilder={publishDatesUrlBuilder}
                  />
                  <ServerSidePaginatedTable<Episode>
                    urlBuilder={episodesUrlBuilder}
                    defaultSort={{ key: 'publishDate', direction: 'desc' }}
                    columns={episodePerformanceColumns}
                    scrollX={PERFORMANCE_TABLE_SCROLL_X}
                    expandedRowRender={(item) => (
                      <ExpandedMetricRowRenderEcho<Episode>
                        item={item}
                        urlBuilder={episodeMetricsBreakdownUrlBuilder}
                        parentTableColumns={episodePerformanceColumns}
                        filters={filters}
                      />
                    )}
                  />
                </Flex>
              </SelectableMetricsView>
            ),
          },
          {
            key: 'post-launch',
            label: 'Post Launch Comparison (All time)',
            children: (
              <SelectableMetricsView>
                <Flex vertical gap="large">
                  <PostLaunchComparison
                    windowDays={launchWindow}
                    onWindowChange={setLaunchWindow}
                    isSynced={isSynced}
                    dataSource="v3"
                  />
                  <ServerSidePaginatedTable<Episode, { windowDays: LaunchWindowDays }>
                    urlBuilder={episodesLaunchWindowUrlBuilder}
                    additionalParams={{ windowDays: launchWindow }}
                    resetKeyAdditionalParams={{ windowDays: 7 }}
                    defaultSort={{ key: 'publishDate', direction: 'desc' }}
                    columns={postLaunchColumns}
                    isSynced={isSynced}
                    scrollX={PERFORMANCE_TABLE_SCROLL_X}
                  />
                </Flex>
              </SelectableMetricsView>
            ),
          },
        ]}
      />
    </CardShellEcho>
  );
};

export { EpisodePerformanceCardEcho, SelectableMetricsView };
